import alt from 'components/Dispatcher';
import {ActionsBase, DispatchAll, IncludeResultCallbacks} from '@store/@types/alt';
import {
  KpiAssignment,
  KpiAssignmentId,
  KpiOrderBy,
  KpiTarget,
  KpiTargetId,
  KpiTargetTree,
} from '../@types/kpi';
import {List, Map} from 'immutable';
import KpiTargetSource, {ModifyKpiAssignmentRequest, ModifyKpiRequest} from './KpiTargetSource';
import generateAsyncActions from 'utils/generateAsyncActions';
import {Moment} from 'moment';
import {UserId} from 'records/User';
import {TeamId} from 'records/TeamId';

/** @link KpiTargetSource
 *  @link KpiTargetStore */
interface AsyncActions {
  fetchGoalKpiTree: () => Promise<List<KpiTargetTree>>;
  fetchUsedByDateAndTeams: (
    start: Moment,
    end: Moment,
    filter: TeamId[] | UserId
  ) => Promise<KpiTarget[]>;
  create: (params: ModifyKpiRequest) => Promise<KpiTarget>;
  update: (id: KpiTargetId, params: ModifyKpiRequest) => Promise<KpiTarget>;
  delete: (id: KpiTargetId) => Promise<void>;
  allAssignments: () => Promise<List<KpiAssignment>>;
  createAssignment: (params: ModifyKpiAssignmentRequest) => Promise<KpiAssignment>;
  updateAssignment: (
    id: KpiAssignmentId,
    params: ModifyKpiAssignmentRequest
  ) => Promise<KpiAssignment>;
  deleteAssignment: (id: KpiAssignmentId) => Promise<void>;
  forceCompleteAssignment: (id: KpiAssignmentId) => Promise<void>;
  completedAssignments: () => Promise<Map<KpiAssignmentId, boolean>>;
}

interface SyncActions {
  unsetKpiSelections(): void;
  toggleKpi(id: KpiTargetId): void;
  setOrder(orderBy: KpiOrderBy): void;
}

export interface KpiTargetActionsInterface
  extends IncludeResultCallbacks<AsyncActions>,
    SyncActions {}

class KpiTargetActions
  extends ActionsBase<AsyncActions, SyncActions>()
  implements DispatchAll<AsyncActions>
{
  constructor() {
    super();
    this.generateActions(
      'toggleKpi',
      'unsetKpiSelections',
      'setOrder',
      ...generateAsyncActions<AsyncActions>(
        'fetchGoalKpiTree',
        'fetchUsedByDateAndTeams',
        'create',
        'update',
        'delete',
        'allAssignments',
        'forceCompleteAssignment',
        'completedAssignments'
      )
    );
  }

  fetchGoalKpiTree() {
    return dispatch => {
      dispatch();
      return KpiTargetSource.fetchGoalKpiTree().then(
        this.fetchGoalKpiTreeSuccess,
        this.fetchGoalKpiTreeError
      );
    };
  }

  fetchUsedByDateAndTeams(start: Moment, end: Moment, filter: TeamId[] | UserId) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.fetchUsedByDateAndTeams(start, end, filter).then(
        this.fetchUsedByDateAndTeamsSuccess,
        this.fetchUsedByDateAndTeamsError
      );
    };
  }

  create(params: ModifyKpiRequest) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.create(params).then(this.createSuccess, this.createError);
    };
  }

  update(id: KpiTargetId, params: ModifyKpiRequest) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.update(id, params).then(this.updateSuccess, this.updateError);
    };
  }

  delete(id: KpiTargetId) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.delete(id).then(() => this.deleteSuccess(id), this.deleteError);
    };
  }

  allAssignments() {
    return dispatch => {
      dispatch();
      return KpiTargetSource.allAssignments().then(
        this.allAssignmentsSuccess,
        this.allAssignmentsError
      );
    };
  }

  createAssignment(params: ModifyKpiAssignmentRequest) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.createAssignment(params).then(
        this.createAssignmentSuccess,
        this.createAssignmentError
      );
    };
  }

  updateAssignment(id: KpiAssignmentId, params: ModifyKpiAssignmentRequest) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.updateAssignment(id, params).then(
        this.updateAssignmentSuccess,
        this.updateAssignmentError
      );
    };
  }

  deleteAssignment(id: KpiAssignmentId) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.deleteAssignment(id).then(
        this.deleteAssignmentSuccess,
        this.deleteAssignmentError
      );
    };
  }

  forceCompleteAssignment(id: KpiAssignmentId) {
    return dispatch => {
      dispatch();
      return KpiTargetSource.forceCompleteAssignment(id).then(
        this.forceCompleteAssignmentSuccess,
        this.forceCompleteAssignmentError
      );
    };
  }

  completedAssignments() {
    return dispatch => {
      dispatch();
      return KpiTargetSource.completedAssignments().then(
        this.completedAssignmentsSuccess,
        this.completedAssignmentsError
      );
    };
  }
}

export default alt.createActions<AsyncActions, SyncActions>(KpiTargetActions);
